import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import OrderedList from "@tiptap/extension-ordered-list"
import { IconArrowLeftOutline18 as ArrowLeft } from "nucleo-ui-outline-18"

import {
	applyProposalSteps,
	computeProposalSteps,
} from "@productlane/lib/src/prosemirror-diff"
import { Button, Textarea, useDarkMode } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { JSONValue } from "@rocicorp/zero"
import type { Editor } from "@tiptap/react"
import { DefaultEditor } from "@/components/editor/default-editor"
import { MetaInfo } from "@/components/meta-info"
import { NucleoFillIconPicker } from "@/components/nucleo-fill-icon-picker"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { isQuerySettled } from "@/lib/query-settled"
import { mutateOk, useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import { HelpCenterArticleProvider } from "./help-center-article-context"
import { HelpCenterProvider } from "./help-center-context"
import { HelpCenterNav } from "./help-center-nav"
import { HelpCenterSuggestionsProvider } from "./use-article-suggestions"

// Mirrors the ProposalConflictError thrown by the zero mutator. Detected by
// name so we don't pull a hard import on the mutator file just for the
// reason field.
function isProposalConflictError(
	err: unknown,
): err is { name: "ProposalConflictError"; reason: string } {
	return (
		!!err &&
		typeof err === "object" &&
		(err as { name?: unknown }).name === "ProposalConflictError" &&
		typeof (err as { reason?: unknown }).reason === "string"
	)
}

export function ProposeEditScreen() {
	// Provider gives the nav's SuggestionsSection a shared bundle without it
	// running its own copy of `useArticleSuggestions`.
	return (
		<HelpCenterSuggestionsProvider>
			<ProposeEditScreenBody />
		</HelpCenterSuggestionsProvider>
	)
}

function ProposeEditScreenBody() {
	const params = useParams({ strict: false })
	const articleId = params.id ?? ""
	const navigate = useNavigate()
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const authData = useAuthData()
	const { isDarkMode } = useDarkMode()

	const [articleGroups, articleGroupsResult] = useQuery(
		queries.helpCenterArticlesWithGroups(),
	)
	const allArticles = articleGroups.flatMap((g) => g.helpCenterArticles)
	const article = allArticles.find((a) => a.id === articleId)
	const group = articleGroups.find((g) => g.id === article?.helpCenterGroupId)

	const [myDrafts, myDraftsResult] = useQuery(queries.myDraftProposals())
	const existingDraft = useMemo(
		() => myDrafts.find((p) => p.articleId === articleId),
		[myDrafts, articleId],
	)

	// Article's existing version chain — the head row is the proposal's anchor.
	const [articleVersions, articleVersionsResult] = useQuery(
		queries.helpCenterArticleVersions({ articleId: articleId, language: "en" }),
	)
	const headVersion = articleVersions[0]

	const [proposalId, setProposalId] = useState<string | null>(null)
	const [title, setTitle] = useState("")
	const [content, setContent] = useState("")
	// HTML snapshot the proposal's steps will be computed against. Captured once
	// at editor init time (or restored from an existing draft) so the diff is
	// stable even if the upstream article changes mid-edit.
	const [baseHtml, setBaseHtml] = useState("")
	const [baseVersionId, setBaseVersionId] = useState<string | null>(null)
	const [isInitialized, setIsInitialized] = useState(false)
	const [isSaving, setIsSaving] = useState(false)

	const tiptapRef = useRef<{ getEditorInstance: () => Editor } | null>(null)

	// Wait until both the article and any existing-draft row are fully synced before
	// populating state — otherwise a late-arriving DRAFT would silently be ignored.
	const queriesReady =
		articleGroupsResult.type === "complete" &&
		myDraftsResult.type === "complete" &&
		articleVersionsResult.type === "complete"

	useEffect(() => {
		if (isInitialized || !article || !queriesReady) return

		const articleHtml =
			typeof article.content === "string" ? article.content : ""

		if (existingDraft) {
			setProposalId(existingDraft.id)
			setTitle(
				typeof existingDraft.proposedTitle === "string"
					? existingDraft.proposedTitle
					: article.title,
			)
			const draftBaseVersion = existingDraft.baseVersion
			const draftBase =
				draftBaseVersion && typeof draftBaseVersion.content === "string"
					? draftBaseVersion.content
					: articleHtml
			setBaseHtml(draftBase)
			setBaseVersionId(existingDraft.baseVersionId ?? headVersion?.id ?? null)
			// Materialize the proposed content from base + steps. Falls back to the
			// base snapshot if the draft has no steps yet (just-created draft).
			let restoredContent = draftBase
			const draftSteps = Array.isArray(existingDraft.proposedSteps)
				? (existingDraft.proposedSteps as unknown[])
				: []
			if (draftSteps.length > 0) {
				try {
					restoredContent = applyProposalSteps(draftBase, draftSteps).html
				} catch (err) {
					console.error("Failed to restore draft from steps", err)
					captureError(err, {
						domain: "general",
						action: "restore docs draft",
						workspaceId: workspace?.id,
						userId: authData.userId,
						extra: { articleId: article?.id },
					})
				}
			}
			setContent(restoredContent)
		} else {
			setProposalId(null)
			setTitle(article.title)
			const headHtml =
				headVersion && typeof headVersion.content === "string"
					? headVersion.content
					: articleHtml
			setBaseHtml(headHtml)
			setBaseVersionId(headVersion?.id ?? null)
			setContent(headHtml)
		}
		setIsInitialized(true)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [article, existingDraft, headVersion, isInitialized, queriesReady])

	const groupNameColor = useMemo(() => {
		return isDarkMode ? workspace?.darkModeAccentColor : workspace?.accentColor
	}, [isDarkMode, workspace])

	if (!workspace) return null

	// Diff the live editor against the captured base snapshot. Pre-cast to
	// `JSONValue` so both create and updateDraft mutators get a consistently
	// typed payload; both call sites previously inlined the same expression.
	const computeStepsForSubmit = (): JSONValue =>
		computeProposalSteps(baseHtml, content) as JSONValue

	const persistNewProposal = async (submitForReview: boolean) => {
		if (!article) return null
		const newId = generateId()
		await mutateOk(
			zero.mutate(
				mutators.helpCenterArticleProposals.create({
					id: newId,
					articleId: article.id,
					baseVersionId: baseVersionId ?? undefined,
					proposedSteps: computeStepsForSubmit(),
					proposedTitle: title !== article.title ? title : undefined,
					submitForReview,
				}),
			),
		)
		return newId
	}

	const persistExistingDraft = async (
		id: string,
		{ submit }: { submit: boolean },
	) => {
		await mutateOk(
			zero.mutate(
				mutators.helpCenterArticleProposals.updateDraft({
					id,
					proposedSteps: computeStepsForSubmit(),
					proposedTitle: title !== (article?.title ?? "") ? title : null,
				}),
			),
		)
		if (submit) {
			await mutateOk(
				zero.mutate(mutators.helpCenterArticleProposals.promoteToOpen({ id })),
			)
		}
	}

	const handleSaveDraft = async () => {
		if (!article) return
		setIsSaving(true)
		try {
			if (proposalId) {
				await persistExistingDraft(proposalId, { submit: false })
			} else {
				const id = await persistNewProposal(false)
				if (id) setProposalId(id)
			}
			analytics.track("helpcenterproposal-save-draft", {
				articleId: article.id,
			})
			toast.success({ title: "Draft saved" })
		} catch (err) {
			console.error(err)
			captureError(err, {
				domain: "general",
				action: "save docs proposal",
				workspaceId: workspace.id,
				userId: authData.userId,
				extra: { articleId: article.id },
			})
			toast.error({ title: "Failed to save draft" })
		} finally {
			setIsSaving(false)
		}
	}

	const handleSubmit = async () => {
		if (!article) return
		setIsSaving(true)
		try {
			if (proposalId) {
				await persistExistingDraft(proposalId, { submit: true })
			} else {
				await persistNewProposal(true)
			}
			analytics.track("helpcenterproposal-submit", { articleId: article.id })
			toast.success({ title: "Proposal submitted for review" })
			void navigate({ to: "/edit-docs/$id", params: { id: article.id } })
		} catch (err) {
			console.error(err)
			if (isProposalConflictError(err)) {
				toast.error({
					title: "Cannot submit proposal",
					description: err.reason,
				})
			} else {
				captureError(err, {
					domain: "general",
					action: "submit docs proposal",
					workspaceId: workspace.id,
					userId: authData.userId,
					extra: { articleId: article.id },
				})
				toast.error({ title: "Failed to submit proposal" })
			}
		} finally {
			setIsSaving(false)
		}
	}

	const onItemSelected = () => {
		/* no-op: this screen has no collapsible nav state to react to */
	}

	if (!article || !group) {
		return (
			<HelpCenterProvider
				onItemSelected={onItemSelected}
				articlesSettled={isQuerySettled(articleGroupsResult)}
			>
				<HelpCenterArticleProvider>
					<div className="flex min-h-0 w-full flex-1 items-center justify-center">
						<div className="text-sm text-gray-500">
							{queriesReady ? "Article not found." : "Loading…"}
						</div>
					</div>
				</HelpCenterArticleProvider>
			</HelpCenterProvider>
		)
	}

	return (
		<HelpCenterProvider
			onItemSelected={onItemSelected}
			articlesSettled={isQuerySettled(articleGroupsResult)}
		>
			<HelpCenterArticleProvider>
				<MetaInfo title={`Propose edit — ${article.title}`} />
				<div className="flex min-h-0 w-full flex-1 overflow-hidden border-r bg-white dark:bg-gray-900">
					<HelpCenterNav
						articleGroups={articleGroups ?? []}
						selectedArticle={article}
						isDetailOpen
					/>
					<div className="flex-shrink flex-grow overflow-hidden bg-white dark:bg-gray-900">
						<div className="flex h-full flex-col">
							<div className="flex max-h-12 min-h-12 w-full items-center justify-between gap-2 border-b border-gray-200 py-2 pl-[17.5px] pr-3 backdrop-blur-xl dark:border-gray-700/80 dark:bg-gray-900/80">
								<button
									type="button"
									onClick={() =>
										void navigate({
											to: "/edit-docs/$id",
											params: { id: article.id },
										})
									}
									className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-350 dark:hover:text-gray-100"
								>
									<ArrowLeft className="h-4 w-4" />
									Back to article
								</button>
								<div className="flex items-center gap-2">
									<span className="mr-2 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-200">
										{existingDraft ? "Editing your draft" : "Proposing an edit"}
									</span>
									<Button
										variant="ghost"
										onClick={handleSaveDraft}
										disabled={isSaving}
									>
										Save as draft
									</Button>
									<Button onClick={handleSubmit} disabled={isSaving}>
										Submit for review
									</Button>
								</div>
							</div>
							<div className="scroll-container docs-scroller flex min-h-0 flex-1 flex-row gap-10 overflow-y-auto px-6 pt-8">
								<div className="mx-auto flex min-w-0 max-w-[652px] flex-1 flex-col">
									<div>
										<div className="flex items-center gap-2">
											<NucleoFillIconPicker
												disabled
												buttonVariant="link"
												icon={article.icon}
												className="h-[1.1rem] min-h-[1rem] w-[1rem] min-w-[1.1rem] text-gray-700 opacity-100 dark:text-gray-300"
												buttonClassName="flex h-8 w-8 items-center justify-center rounded bg-gray-100 dark:bg-gray-800"
												onChange={() => {
													/* icon not editable in proposals (MVP) */
												}}
											/>
											<span
												className="text-sm"
												style={{ color: groupNameColor ?? undefined }}
											>
												{group.name}
											</span>
										</div>
										<Textarea
											name="proposal-title"
											variant="borderless"
											noResize
											textSize="xxl"
											placeholder="Title"
											className="no-scrollbar mb-2 mt-4 w-full bg-transparent"
											style={{ lineHeight: "1.2" }}
											fontWeight="bold"
											maxLength={200}
											value={title}
											onChange={(e) => setTitle(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													if (!tiptapRef.current) return
													e.preventDefault()
													tiptapRef.current
														.getEditorInstance()
														.commands.focus("start")
												}
											}}
										/>
									</div>
									{isInitialized && (
										<DefaultEditor
											extensions={[
												OrderedList.configure({
													HTMLAttributes: {
														class: "steps-list",
													},
												}),
											]}
											autofocus={false}
											initialContent={content}
											placeholder="Edit the article content..."
											onUpdate={({ editor }) => {
												setContent(editor.getHTML())
											}}
											editable
											className="mt-4 w-full max-w-[min(630px,100%)] flex-grow pb-32"
											ref={tiptapRef}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</HelpCenterArticleProvider>
		</HelpCenterProvider>
	)
}
